<template>
    <modals-generic
    preventDetailsModalClose
    applySecondLayer
    :sizeClass="'medium'"
    :headerColor="headerColor"
    :icon="headerTitleIcon"
    :title="t('bankassurance.policies.details.receipts.modalTitle', {number: selectedReceipt.receiptNumber})"
    :subTitle="selectedReceipt.situation"
    :headerLeftButtonText="t('bankassurance.policies.details.receipts.backToReceipts')"
    @hideMe="emit('hideMe')">
        <div class="group-wrapper" v-if="!showSpinner && !applyErrorState">
            <!-- *********************************************
            *                Receipt Information             *
            ********************************************** -->
            <divider-generic :icon="'document'" :applyBorder="false" :title="t('bankassurance.policies.details.receipts.receiptData')"/>
            <div v-if="documentsTypeParser.length" class="buttons-wrapper">
                <button-tertiary
                v-for="(button, index) in documentsTypeParser"
                :key="button"
                icon="download"
                :buttonSize="'small'"
                :class="{'last-button': index === (documentsTypeParser.length - 1)}"
                :label="button.label"
                @clicked="downloadLink = createDownloadPolicyDocumentUrl({
                    insuranceId: selectedPolicy.insuranceId,
                    receiptId: button.receiptId,
                    docType: button.docType
                })"/>
            </div>
            <text-block-generic
            v-for="(information, index) in receiptInformationParser"
            :key="index"
            applyEllipsis
            :params="information"
            :icon="information.icon"
            :colorIconPreset="information.colorIcon"/>
            <!-- *********************************************
            *                     Parcel List                *
            ********************************************** -->
            <divider-generic :icon="'bills'" :title="t('bankassurance.policies.details.receipts.portions')"/>
            <table-generic
            noHoverStyles
            :hasDetail="false"
            :headerData="parcelsHeaderData"
            :tableStyles="parcelsTableStyles"
            :tableData="parcelsTableData"/>
            <div v-if="getMobileAppState" class="mobile-parcel-total-container">
                {{ t('bankassurance.policies.details.receipts.tableHeaders.total') }}
                <span class="parcel-total">
                    {{ amountFormat(receiptDetails.parcelTotal.value, receiptDetails.parcelTotal.currency) }}
                </span>
            </div>
        </div>
        <!-- Loader Generic -->
        <loaders-generic v-if="showSpinner" fullSize/>
        <!-- Detail Error State -->
        <empty-state-error v-if="applyErrorState && !showSpinner" :message="t('bankassurance.policies.details.receipts.errorState')"/>
    </modals-generic>
    <!-- Error Feedback -->
    <toasts-generic
    v-if="showErrorToast"
    toastType="error"
    :feedbackText="errorToastData.text"
    :buttonsParams="errorToastData.buttons"
    @hideMe="showErrorToast = false"/>
    <!-- Component to handle download logic-->
	<download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
</template>

<script setup>
import ModalsGeneric from '@/components/generic/modals/side/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { receiptsStatusBackgroundColor, receiptsStatusIcon, receiptsIconColorMapper } from '@/assets/scripts/mappers/policies'
import { titleCaseBuilder } from '@/assets/scripts/strings'
import { getDate } from '@/assets/scripts/dateBuilders'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createDownloadPolicyDocumentUrl } from '@/network/api/insurance.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestReceiptDetail, requestPolicyReceiptDocumentsList } = mapActions(moduleMapper.BANKASSURANCE_POLICIES)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['hideMe'])

const props = defineProps({
    selectedPolicy: Array,
    selectedReceipt: Array
})

/* Data */
const showSpinner = ref(false)

/*************************************************
*                                                *
*                     Modal                      *
*                                                *
*************************************************/
const receiptDetails = ref([])
const documentsList = ref([])
const downloadLink = ref(null)

const headerColor = computed(() => {
    return receiptsStatusBackgroundColor(props.selectedReceipt.situationId)
})

const headerTitleIcon = computed(() => {
    return receiptsStatusIcon(props.selectedReceipt.situationId)
})

const headerButtonSubText = computed(() => {
    return props.selectedPolicy.insuranceCode.toLowerCase() === 'auto' ? titleCaseBuilder(props.selectedPolicy.insuranceCode) : t('bankassurance.policies.details.receipts.travel')
})

/*************************************************
*                                                *
*                  Receipt Info                  *
*                                                *
*************************************************/
const documentsTypeParser = computed(() => {
    return documentsList.value.map(button => {
        return {
            label: button.nameDocType,
            docType: button.docType,
            receiptId: button.documentId
        }
    })
})

const receiptInformationParser = computed(() => {
    return [{
        label: t('bankassurance.policies.details.receipts.receiptNumber'),
        content: receiptDetails.value.receiptNumber ? receiptDetails.value.receiptNumber : '-'
    }, {
        label: t('bankassurance.policies.details.receipts.date'),
        content: receiptDetails.value.dateFrom ? t('bankassurance.policies.details.policyInfo.receiptDate', { from: getDate(receiptDetails.value.dateFrom), to: getDate(receiptDetails.value.dateTo) }) : '-',
        classes: 'capitalize'
    }, {
        label: t('bankassurance.policies.details.receipts.situation'),
        content: receiptDetails.value.situation ? receiptDetails.value.situation : '-',
        icon: receiptsStatusIcon(receiptDetails.value.situationCode),
        colorIcon: receiptsIconColorMapper(receiptDetails.value.situationCode)
    }, {
        label: t('bankassurance.policies.details.receipts.amount'),
        content: amountFormat(receiptDetails.value.totalValue ? receiptDetails.value.totalValue.value : '-', receiptDetails.value.totalValue ? receiptDetails.value.totalValue.currency : '-')
    }, {
        label: t('bankassurance.policies.details.receipts.fractionation'),
        content: receiptDetails.value.fractioning ? receiptDetails.value.fractioning : '-'
    }, {
        label: t('bankassurance.policies.details.receipts.emissionDate'),
        content: receiptDetails.value.emissionDate ? getDate(receiptDetails.value.emissionDate) : '-',
        classes: 'capitalize'
    }, {
        label: t('bankassurance.policies.details.receipts.billingType'),
        content: receiptDetails.value.collectingType ? receiptDetails.value.collectingType : '-'
    }, {
        label: t('bankassurance.policies.details.receipts.receiptKind'),
        content: receiptDetails.value.receiptKind ? receiptDetails.value.receiptKind : '-'
    }, {
        label: t('bankassurance.policies.details.receipts.annuity'),
        content: receiptDetails.value.annuity ? receiptDetails.value.annuity : '-'
    }, {
        label: t('bankassurance.policies.details.receipts.dueDate'),
        content: receiptDetails.value.dueDate ? getDate(receiptDetails.value.dueDate) : '-',
        classes: 'capitalize'
    }]
})

const parcelsHeaderData = ([
    { label: t('bankassurance.policies.details.receipts.tableHeaders.portion'), dataAttr: 'title', showInMobile: { groupWith: ['amount'] }, rowCellType: 'title' },
    { label: t('bankassurance.policies.details.receipts.tableHeaders.value'), dataAttr: 'amount', rowCellType: 'number' }
])

const parcelsTableStyles = [
	null,
    { 'text-align': 'end' }
]

const parcelsTableData = computed(() => {
	const tableData = receiptDetails.value.parcelEntries.map(parcel => {
		return [
			{ value: parcel.code, dataAttr: 'id' },
			{
				type: 'title',
				value: parcel.description,
				dataAttr: 'title'
			},
			{
                type: 'amount',
				value: { content: parcel.currencyValue.value, currency: parcel.currencyValue.currency, noBalanceIcon: true, hasArrowIcons: false },
				dataAttr: 'amount'
			}
		]
	})

    if (!getMobileAppState.value) {
        tableData[tableData.length] = [
                {
                    type: 'title',
                    value: t('bankassurance.policies.details.receipts.tableHeaders.total'),
                    dataAttr: 'title'
                },
                {
                    type: 'amount',
                    value: { content: receiptDetails.value.parcelTotal.value, currency: receiptDetails.value.parcelTotal.currency, noBalanceIcon: true, hasArrowIcons: false },
                    dataAttr: 'title'
                }
            ]
    }

    return tableData
})

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showErrorToast = ref(false)
const applyErrorState = ref(false)

const errorToastData = ref({
	text: '',
	buttons: [{
        text: t('commonButtons.ignore'),
        callbackAction: () => { showErrorToast.value = false }
    },
    {
        text: t('commonButtons.tryAgain'),
        callbackAction: ''
    }]
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestDetails = async () => {
	showSpinner.value = true
	showErrorToast.value = false
    applyErrorState.value = false

    const payload = {
        insuranceId: props.selectedPolicy.insuranceId,
        receiptId: props.selectedReceipt.receiptNumber
    }

	try {
        const details = await requestReceiptDetail(payload)
        const documents = await requestPolicyReceiptDocumentsList(payload)
        receiptDetails.value = details.data.data
        documentsList.value = documents.data.data
        showSpinner.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showSpinner.value = false
		errorHandler(error.response, requestDetails)
	}
}

const errorHandler = (data, retryFn) => {
	errorToastData.value.text = data && data.message ? data.message : t('genericErrorMessage')
	errorToastData.value.buttons[1].callbackAction = retryFn
	showErrorToast.value = true
    applyErrorState.value = true
}

requestDetails()
</script>

<style lang="scss" scoped>
.buttons-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;

    :deep(.tertiary-button-wrapper) {
        width: fit-content;
        min-width: unset;
        max-width: 180px;
    }
}

.mobile-parcel-total-container {
    width: 100%;
    min-height: 56px;

    display: flex;
    justify-content: space-between;

    font-size: 16px;
    font-weight: 500;
    color: var(--primary);

    .parcel-total {
        font-size: 15px;
        font-weight: 500;
        color: var(--secondary-dark-1);
    }
}
</style>
